import { useState, useEffect, useCallback } from 'preact/hooks'
import type { CloudProject, CloudTask } from '../../types'
import { TASK_STATUS_LABELS, TASK_PRIORITY_LABELS, TASK_PRIORITY_COLORS, TASK_TYPE_LABELS } from '../../types'
import {
  listCloudProjects,
  listCloudTasks,
  upsertCloudProject,
  deleteCloudProject,
  upsertCloudTask,
  deleteCloudTask,
} from '../../services/api'
import { formatDate, EmptyState, DetailRow, detailPanelStyle, refreshBtnStyle } from './shared'
import {
  ConfirmDialog,
  ModalDialog,
  FormField,
  SaveButton,
  DeleteButton,
  CreateButton,
  InlineInput,
  inputStyle,
  selectStyle,
} from './CrudHelpers'

const PROJECT_COLORS = ['#2DA5A0', '#4fc3f7', '#81c784', '#ff9800', '#f44336', '#9c27b0', '#e91e63', '#00bcd4']

const PRIORITY_OPTIONS = Object.entries(TASK_PRIORITY_LABELS).map(([v, l]) => ({ value: v, label: l }))
const STATUS_OPTIONS = Object.entries(TASK_STATUS_LABELS).map(([v, l]) => ({ value: v, label: l }))
const TYPE_OPTIONS = Object.entries(TASK_TYPE_LABELS).map(([v, l]) => ({ value: v, label: l }))

/** Status values that count as completed/terminal — mirrors TasksView. */
const TERMINAL_STATUSES = new Set([2, 3, 5])
/** Status the quick-complete checkbox toggles to. 5 = Done in this app. */
const COMPLETE_STATUS = 5

/** Sort tasks within the project tasks list — same ordering as TasksView's
 *  per-section sort: due date asc, then priority desc, then title. Tasks
 *  without a due date go to the end. */
function sortTasksByDueAndPriority(tasks: CloudTask[]): CloudTask[] {
  return [...tasks].sort((a, b) => {
    const aDone = TERMINAL_STATUSES.has(a.status ?? 0) ? 1 : 0
    const bDone = TERMINAL_STATUSES.has(b.status ?? 0) ? 1 : 0
    if (aDone !== bDone) return aDone - bDone // active first
    const aTime = a.dueDate ? new Date(a.dueDate).getTime() : Number.MAX_SAFE_INTEGER
    const bTime = b.dueDate ? new Date(b.dueDate).getTime() : Number.MAX_SAFE_INTEGER
    if (aTime !== bTime) return aTime - bTime
    const aPrio = a.priority ?? 0
    const bPrio = b.priority ?? 0
    if (aPrio !== bPrio) return bPrio - aPrio
    return (a.title || '').localeCompare(b.title || '')
  })
}

/** What the right column is currently showing for the selected project. */
type RightView = 'tasks' | 'editTask' | 'editProject'

/** Override of the shared detailPanelStyle: instead of the fixed 360px
 *  width that TasksView's narrow editor uses, the project right column
 *  flexes equally with the project grid (50/50). */
const wideDetailPanelStyle: Record<string, string | number> = {
  ...detailPanelStyle,
  width: 'auto',
  flex: 1,
  minWidth: 0,
}

export function ProjectsView() {
  const [projects, setProjects] = useState<CloudProject[]>([])
  const [tasks, setTasks] = useState<CloudTask[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedProject, setSelectedProject] = useState<CloudProject | null>(null)
  const [rightView, setRightView] = useState<RightView>('tasks')

  // Project edit state — only used while rightView === 'editProject'
  const [editingProject, setEditingProject] = useState<CloudProject | null>(null)
  const [projectDirty, setProjectDirty] = useState(false)
  const [projectSaving, setProjectSaving] = useState(false)

  // Task edit state — used while rightView === 'editTask'
  const [editingTask, setEditingTask] = useState<CloudTask | null>(null)
  const [taskDirty, setTaskDirty] = useState(false)
  const [taskSaving, setTaskSaving] = useState(false)

  const [showCreateProject, setShowCreateProject] = useState(false)
  const [showCreateTask, setShowCreateTask] = useState(false)
  const [confirmDeleteProject, setConfirmDeleteProject] = useState(false)
  const [confirmDeleteTask, setConfirmDeleteTask] = useState<CloudTask | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const [p, t] = await Promise.all([listCloudProjects(), listCloudTasks()])
      setProjects(p)
      setTasks(t)
    } catch (e) {
      console.error('Failed to load projects:', e)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load() }, [load])

  const getTaskCounts = (projectId: string) => {
    const projectTasks = tasks.filter((t) => t.projectId === projectId)
    const completed = projectTasks.filter((t) => TERMINAL_STATUSES.has(t.status ?? 0)).length
    return { total: projectTasks.length, completed }
  }

  // ── Project actions ──────────────────────────────────────────────

  const handleSelectProject = (project: CloudProject) => {
    if (selectedProject?.id === project.id && rightView === 'tasks') {
      // toggle off
      setSelectedProject(null)
      setEditingTask(null)
      return
    }
    setSelectedProject(project)
    setRightView('tasks')
    setEditingTask(null)
    setTaskDirty(false)
  }

  const openEditProject = () => {
    if (!selectedProject) return
    setEditingProject({ ...selectedProject })
    setProjectDirty(false)
    setRightView('editProject')
  }

  const updateProjectField = (field: string, value: unknown) => {
    if (!editingProject) return
    setEditingProject({ ...editingProject, [field]: value })
    setProjectDirty(true)
  }

  const handleSaveProject = async () => {
    if (!editingProject) return
    setProjectSaving(true)
    try {
      const updated = { ...editingProject, updated_at: new Date().toISOString() }
      await upsertCloudProject(updated)
      setProjects((prev) => prev.map((p) => p.id === updated.id ? updated : p))
      setSelectedProject(updated)
      setEditingProject({ ...updated })
      setProjectDirty(false)
    } catch (e) {
      console.error('Failed to save project:', e)
    } finally {
      setProjectSaving(false)
    }
  }

  const handleDeleteProject = async () => {
    if (!selectedProject) return
    try {
      await deleteCloudProject(selectedProject.id)
      setProjects((prev) => prev.filter((p) => p.id !== selectedProject.id))
      setSelectedProject(null)
      setEditingProject(null)
      setConfirmDeleteProject(false)
    } catch (e) {
      console.error('Failed to delete project:', e)
    }
  }

  // ── Task actions ─────────────────────────────────────────────────

  const openEditTask = (task: CloudTask) => {
    setEditingTask({ ...task })
    setTaskDirty(false)
    setRightView('editTask')
  }

  const updateTaskField = (field: string, value: unknown) => {
    if (!editingTask) return
    setEditingTask({ ...editingTask, [field]: value })
    setTaskDirty(true)
  }

  const handleSaveTask = async () => {
    if (!editingTask) return
    setTaskSaving(true)
    try {
      const updated = { ...editingTask, lastUpdatedAt: new Date().toISOString() }
      await upsertCloudTask(updated)
      setTasks((prev) => prev.map((t) => t.id === updated.id ? updated : t))
      setEditingTask({ ...updated })
      setTaskDirty(false)
    } catch (e) {
      console.error('Failed to save task:', e)
    } finally {
      setTaskSaving(false)
    }
  }

  // Optimistic quick-complete toggle from the task list. Doesn't open the
  // editor — flips status (Not Started ↔ Done) and persists. Reverts the
  // optimistic update on failure.
  const toggleTaskComplete = async (task: CloudTask) => {
    const isDone = TERMINAL_STATUSES.has(task.status ?? 0)
    const updated: CloudTask = {
      ...task,
      status: isDone ? 0 : COMPLETE_STATUS,
      completedAt: isDone ? undefined : new Date().toISOString(),
      lastUpdatedAt: new Date().toISOString(),
    }
    setTasks((prev) => prev.map((t) => t.id === task.id ? updated : t))
    if (editingTask?.id === task.id) setEditingTask({ ...updated })
    try {
      await upsertCloudTask(updated)
    } catch (e) {
      console.error('Failed to toggle complete:', e)
      // Revert on failure
      setTasks((prev) => prev.map((t) => t.id === task.id ? task : t))
      if (editingTask?.id === task.id) setEditingTask({ ...task })
    }
  }

  const handleDeleteTask = async (task: CloudTask) => {
    try {
      await deleteCloudTask(task.id)
      setTasks((prev) => prev.filter((t) => t.id !== task.id))
      if (editingTask?.id === task.id) {
        setEditingTask(null)
        setRightView('tasks')
      }
      setConfirmDeleteTask(null)
    } catch (e) {
      console.error('Failed to delete task:', e)
    }
  }

  // Tasks belonging to the currently-selected project (events excluded —
  // they belong on the calendar, mirroring TasksView).
  const projectTasks = selectedProject
    ? sortTasksByDueAndPriority(tasks.filter((t) => t.projectId === selectedProject.id && (t.type ?? 0) !== 1))
    : []

  return (
    <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
      {/* Project grid */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 16px', borderBottom: '1px solid rgba(250,250,250,0.1)' }}>
          <span style={{ flex: 1, fontSize: 13, color: 'rgba(250,250,250,0.5)' }}>{projects.length} projects</span>
          <CreateButton label="New Project" onClick={() => setShowCreateProject(true)} />
          <button onClick={load} style={refreshBtnStyle} disabled={loading}>
            {loading ? '...' : 'Refresh'}
          </button>
        </div>

        <div style={{ flex: 1, overflow: 'auto', padding: 16 }}>
          {loading && <div style={{ padding: 24, textAlign: 'center', color: 'rgba(250,250,250,0.4)' }}>Loading...</div>}
          {!loading && projects.length === 0 && <EmptyState label="No projects yet" />}
          {!loading && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16 }}>
              {projects.map((project) => {
                const { total, completed } = getTaskCounts(project.id)
                const color = PROJECT_COLORS[project.color_index ?? 0] || PROJECT_COLORS[0]
                const progress = total > 0 ? (completed / total) * 100 : 0

                return (
                  <div
                    key={project.id}
                    onClick={() => handleSelectProject(project)}
                    style={{
                      ...projectCardStyle,
                      cursor: 'pointer',
                      border: selectedProject?.id === project.id ? '1px solid #2DA5A0' : '1px solid rgba(250,250,250,0.1)',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                      <div style={{ width: 12, height: 12, borderRadius: 3, background: color, flexShrink: 0 }} />
                      <span style={{ flex: 1, fontWeight: 600, fontSize: 16 }}>{project.name}</span>
                      {project.is_completed ? (
                        <span style={{ fontSize: 11, color: '#4caf50' }}>Completed</span>
                      ) : (
                        <span style={{ fontSize: 11, color: 'rgba(250,250,250,0.4)' }}>Active</span>
                      )}
                    </div>
                    {project.description && (
                      <div style={{ fontSize: 13, color: 'rgba(250,250,250,0.6)', marginBottom: 12, lineHeight: 1.5 }}>
                        {project.description}
                      </div>
                    )}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                      <div style={{ flex: 1, height: 6, background: 'rgba(250,250,250,0.1)', borderRadius: 3, overflow: 'hidden' }}>
                        <div style={{ height: '100%', width: `${progress}%`, background: color, borderRadius: 3 }} />
                      </div>
                      <span style={{ fontSize: 12, color: 'rgba(250,250,250,0.5)', flexShrink: 0 }}>
                        {completed}/{total}
                      </span>
                    </div>
                    <div style={{ fontSize: 11, color: 'rgba(250,250,250,0.35)' }}>
                      Created {formatDate(project.created_at)}
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </div>

      {/* Right column — three modes: tasks list, edit task, edit project */}
      {selectedProject && rightView === 'tasks' && (
        <ProjectTasksPanel
          project={selectedProject}
          tasks={projectTasks}
          onClose={() => { setSelectedProject(null); setEditingTask(null) }}
          onEditProject={openEditProject}
          onAddTask={() => setShowCreateTask(true)}
          onOpenTask={openEditTask}
          onToggleComplete={toggleTaskComplete}
          onDeleteTask={(t) => setConfirmDeleteTask(t)}
        />
      )}

      {selectedProject && rightView === 'editTask' && editingTask && (
        <TaskEditorPanel
          task={editingTask}
          dirty={taskDirty}
          saving={taskSaving}
          onBack={() => { setRightView('tasks'); setEditingTask(null); setTaskDirty(false) }}
          onChange={updateTaskField}
          onSave={handleSaveTask}
          onDelete={() => editingTask && setConfirmDeleteTask(editingTask)}
          onSubtaskAdd={(title) => {
            if (!editingTask) return
            const newSub = { id: crypto.randomUUID(), title, isCompleted: false, createdAt: new Date().toISOString() }
            updateTaskField('subTasks', [...(editingTask.subTasks || []), newSub])
          }}
          onSubtaskToggle={(idx) => {
            if (!editingTask) return
            const list = [...(editingTask.subTasks || [])]
            list[idx] = { ...list[idx], isCompleted: !list[idx].isCompleted }
            updateTaskField('subTasks', list)
          }}
          onSubtaskRemove={(idx) => {
            if (!editingTask) return
            const list = (editingTask.subTasks || []).filter((_, i) => i !== idx)
            updateTaskField('subTasks', list)
          }}
        />
      )}

      {selectedProject && rightView === 'editProject' && editingProject && (
        <ProjectEditorPanel
          project={editingProject}
          dirty={projectDirty}
          saving={projectSaving}
          taskCounts={getTaskCounts(editingProject.id)}
          onBack={() => { setRightView('tasks'); setProjectDirty(false) }}
          onChange={updateProjectField}
          onSave={handleSaveProject}
          onDelete={() => setConfirmDeleteProject(true)}
        />
      )}

      {/* Create project modal */}
      {showCreateProject && (
        <CreateProjectModal
          onClose={() => setShowCreateProject(false)}
          onCreate={async (project) => {
            await upsertCloudProject(project)
            setProjects((prev) => [project, ...prev])
            setShowCreateProject(false)
          }}
        />
      )}

      {/* Create task modal — pre-fills the project to the currently-open one */}
      {showCreateTask && selectedProject && (
        <CreateTaskModal
          defaultProjectId={selectedProject.id}
          onClose={() => setShowCreateTask(false)}
          onCreate={async (task) => {
            await upsertCloudTask(task)
            setTasks((prev) => [task, ...prev])
            setShowCreateTask(false)
          }}
        />
      )}

      {/* Confirm dialogs */}
      {confirmDeleteProject && selectedProject && (
        <ConfirmDialog
          message={`Delete project "${selectedProject.name}"? Tasks associated with this project will not be deleted.`}
          onConfirm={handleDeleteProject}
          onCancel={() => setConfirmDeleteProject(false)}
        />
      )}
      {confirmDeleteTask && (
        <ConfirmDialog
          message={`Delete task "${confirmDeleteTask.title || 'Untitled'}"?`}
          onConfirm={() => handleDeleteTask(confirmDeleteTask)}
          onCancel={() => setConfirmDeleteTask(null)}
        />
      )}
    </div>
  )
}

// ── Right-column: project tasks list ──────────────────────────────

function ProjectTasksPanel({
  project,
  tasks,
  onClose,
  onEditProject,
  onAddTask,
  onOpenTask,
  onToggleComplete,
  onDeleteTask,
}: {
  project: CloudProject
  tasks: CloudTask[]
  onClose: () => void
  onEditProject: () => void
  onAddTask: () => void
  onOpenTask: (task: CloudTask) => void
  onToggleComplete: (task: CloudTask) => void
  onDeleteTask: (task: CloudTask) => void
}) {
  const color = PROJECT_COLORS[project.color_index ?? 0] || PROJECT_COLORS[0]
  const completed = tasks.filter((t) => TERMINAL_STATUSES.has(t.status ?? 0)).length
  const total = tasks.length

  return (
    <div style={wideDetailPanelStyle}>
      <div style={{ padding: 16, borderBottom: '1px solid rgba(250,250,250,0.1)', display: 'flex', alignItems: 'center', gap: 8 }}>
        <div style={{ width: 12, height: 12, borderRadius: 3, background: color, flexShrink: 0 }} />
        <span style={{ flex: 1, fontWeight: 600, fontSize: 14 }}>{project.name}</span>
        <span style={{ fontSize: 11, color: 'rgba(250,250,250,0.5)' }}>{completed}/{total}</span>
        <button
          onClick={onEditProject}
          style={{ ...refreshBtnStyle, borderColor: 'rgba(45,165,160,0.4)', color: '#2DA5A0' }}
          title="Edit project"
        >
          Edit Project
        </button>
        <CreateButton label="New Task" onClick={onAddTask} />
        <button onClick={onClose} style={refreshBtnStyle}>X</button>
      </div>

      {project.description && (
        <div style={{ padding: '8px 16px', fontSize: 13, color: 'rgba(250,250,250,0.6)', lineHeight: 1.5, whiteSpace: 'pre-wrap', borderBottom: '1px solid rgba(250,250,250,0.05)' }}>
          {project.description}
        </div>
      )}

      <div style={{ flex: 1, overflow: 'auto', padding: '8px 16px' }}>
        {tasks.length === 0 && <EmptyState label="No tasks in this project" />}
        {tasks.map((task) => {
          const isDone = TERMINAL_STATUSES.has(task.status ?? 0)
          return (
            <div
              key={task.id}
              style={{
                ...taskRowStyle,
                borderLeft: `3px solid ${TASK_PRIORITY_COLORS[task.priority ?? 0] || '#555'}`,
                opacity: isDone ? 0.55 : 1,
              }}
            >
              <input
                type="checkbox"
                checked={isDone}
                onClick={(e) => e.stopPropagation()}
                onChange={() => onToggleComplete(task)}
                title={isDone ? 'Mark incomplete' : 'Mark complete'}
                style={{ marginRight: 8, cursor: 'pointer' }}
              />
              <div
                onClick={() => onOpenTask(task)}
                style={{ flex: 1, cursor: 'pointer', minWidth: 0 }}
                title="Click to edit"
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {task.emoji && <span>{task.emoji}</span>}
                  <span style={{
                    flex: 1,
                    fontWeight: 500,
                    textDecoration: isDone ? 'line-through' : 'none',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}>
                    {task.title || 'Untitled'}
                  </span>
                  {(task.priority ?? 0) > 0 && (
                    <span style={badgeStyle(TASK_PRIORITY_COLORS[task.priority!] || '#666')}>
                      {TASK_PRIORITY_LABELS[task.priority!]}
                    </span>
                  )}
                </div>
                {task.dueDate && (
                  <div style={{ fontSize: 12, color: 'rgba(250,250,250,0.5)', marginTop: 4 }}>
                    Due: {formatDate(task.dueDate)}
                  </div>
                )}
              </div>
              <button
                onClick={(e) => { e.stopPropagation(); onDeleteTask(task) }}
                title="Delete task"
                style={{
                  background: 'none', border: 'none', color: 'rgba(250,250,250,0.4)',
                  cursor: 'pointer', fontSize: 16, padding: '4px 8px',
                }}
              >
                ×
              </button>
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ── Right-column: inline task editor ──────────────────────────────

function TaskEditorPanel({
  task,
  dirty,
  saving,
  onBack,
  onChange,
  onSave,
  onDelete,
  onSubtaskAdd,
  onSubtaskToggle,
  onSubtaskRemove,
}: {
  task: CloudTask
  dirty: boolean
  saving: boolean
  onBack: () => void
  onChange: (field: string, value: unknown) => void
  onSave: () => void
  onDelete: () => void
  onSubtaskAdd: (title: string) => void
  onSubtaskToggle: (idx: number) => void
  onSubtaskRemove: (idx: number) => void
}) {
  return (
    <div style={wideDetailPanelStyle}>
      <div style={{ padding: 16, borderBottom: '1px solid rgba(250,250,250,0.1)', display: 'flex', alignItems: 'center', gap: 8 }}>
        <button onClick={onBack} style={refreshBtnStyle} title="Back to project tasks">{'←'} Tasks</button>
        <span style={{ flex: 1, fontWeight: 600, fontSize: 14, color: 'rgba(250,250,250,0.5)' }}>Edit Task</span>
        <SaveButton dirty={dirty} saving={saving} onClick={onSave} />
        <DeleteButton onClick={onDelete} />
      </div>
      <div style={{ flex: 1, overflow: 'auto', padding: 16 }}>
        <div style={{ marginBottom: 12 }}>
          <label style={fieldLabelStyle}>Title</label>
          <InlineInput value={task.title || ''} onChange={(v) => onChange('title', v)} placeholder="Task title" />
        </div>
        <div style={{ marginBottom: 12 }}>
          <label style={fieldLabelStyle}>Description</label>
          <InlineInput type="textarea" value={task.description || ''} onChange={(v) => onChange('description', v)} placeholder="Description" />
        </div>
        <div style={{ marginBottom: 12 }}>
          <label style={fieldLabelStyle}>Status</label>
          <select
            style={selectStyle}
            value={String(task.status ?? 0)}
            onChange={(e) => onChange('status', Number((e.target as HTMLSelectElement).value))}
          >
            {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
        <div style={{ marginBottom: 12 }}>
          <label style={fieldLabelStyle}>Priority</label>
          <select
            style={selectStyle}
            value={String(task.priority ?? 0)}
            onChange={(e) => onChange('priority', Number((e.target as HTMLSelectElement).value))}
          >
            {PRIORITY_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
        <div style={{ marginBottom: 12 }}>
          <label style={fieldLabelStyle}>Type</label>
          <select
            style={selectStyle}
            value={String(task.type ?? 0)}
            onChange={(e) => onChange('type', Number((e.target as HTMLSelectElement).value))}
          >
            {TYPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
        <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
          <div style={{ flex: 1 }}>
            <label style={fieldLabelStyle}>Due Date</label>
            <input
              type="date"
              style={inputStyle}
              value={task.dueDate ? task.dueDate.slice(0, 10) : ''}
              onInput={(e) => {
                const date = (e.target as HTMLInputElement).value
                const oldTime = task.dueDate?.slice(11, 16) || '00:00'
                onChange('dueDate', date ? new Date(`${date}T${oldTime}`).toISOString() : undefined)
              }}
            />
          </div>
          <div style={{ flex: 1 }}>
            <label style={fieldLabelStyle}>Due Time</label>
            <input
              type="time"
              style={inputStyle}
              value={task.dueDate && task.dueDate.length > 10 ? task.dueDate.slice(11, 16) : ''}
              onInput={(e) => {
                const time = (e.target as HTMLInputElement).value
                const oldDate = task.dueDate?.slice(0, 10) || new Date().toISOString().slice(0, 10)
                onChange('dueDate', time ? new Date(`${oldDate}T${time}`).toISOString() : undefined)
              }}
            />
          </div>
        </div>
        <div style={{ marginBottom: 12 }}>
          <label style={fieldLabelStyle}>Location</label>
          <InlineInput value={task.location || ''} onChange={(v) => onChange('location', v || undefined)} placeholder="Location" />
        </div>
        <div style={{ marginBottom: 12 }}>
          <label style={fieldLabelStyle}>Tags</label>
          <InlineInput
            value={(task.tags || []).join(', ')}
            onChange={(v) => onChange('tags', v ? v.split(',').map((t) => t.trim()).filter(Boolean) : [])}
            placeholder="tag1, tag2, ..."
          />
        </div>
        <div style={{ marginBottom: 12 }}>
          <label style={fieldLabelStyle}>Emoji</label>
          <InlineInput
            value={task.emoji || ''}
            onChange={(v) => onChange('emoji', v || undefined)}
            placeholder="\u{1F4DD}"
            style={{ width: 60 }}
          />
        </div>

        {/* Subtasks */}
        <div style={{ marginTop: 12 }}>
          <label style={fieldLabelStyle}>
            Subtasks{task.subTasks && task.subTasks.length > 0
              ? ` (${task.subTasks.filter((s) => s.isCompleted).length}/${task.subTasks.length})`
              : ''}
          </label>
          {(task.subTasks || []).map((st, i) => (
            <div key={st.id} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
              <input
                type="checkbox"
                checked={st.isCompleted}
                onChange={() => onSubtaskToggle(i)}
              />
              <span style={{ fontSize: 13, color: st.isCompleted ? '#4caf50' : 'rgba(250,250,250,0.7)', flex: 1, textDecoration: st.isCompleted ? 'line-through' : 'none' }}>
                {st.title}
              </span>
              <button
                onClick={() => onSubtaskRemove(i)}
                style={{ background: 'none', border: 'none', color: 'rgba(250,250,250,0.3)', cursor: 'pointer', fontSize: 14 }}
                title="Remove subtask"
              >
                x
              </button>
            </div>
          ))}
          <AddSubtaskInput onAdd={onSubtaskAdd} />
        </div>

        <div style={{ marginTop: 16, paddingTop: 12, borderTop: '1px solid rgba(250,250,250,0.1)' }}>
          <DetailRow label="Created" value={formatDate(task.createdAt)} />
          <DetailRow label="Updated" value={formatDate(task.lastUpdatedAt)} />
          {task.completedAt && <DetailRow label="Completed" value={formatDate(task.completedAt)} />}
        </div>
      </div>
    </div>
  )
}

// ── Right-column: project editor ──────────────────────────────────

function ProjectEditorPanel({
  project,
  dirty,
  saving,
  taskCounts,
  onBack,
  onChange,
  onSave,
  onDelete,
}: {
  project: CloudProject
  dirty: boolean
  saving: boolean
  taskCounts: { total: number; completed: number }
  onBack: () => void
  onChange: (field: string, value: unknown) => void
  onSave: () => void
  onDelete: () => void
}) {
  return (
    <div style={wideDetailPanelStyle}>
      <div style={{ padding: 16, borderBottom: '1px solid rgba(250,250,250,0.1)', display: 'flex', alignItems: 'center', gap: 8 }}>
        <button onClick={onBack} style={refreshBtnStyle} title="Back to project tasks">{'←'} Tasks</button>
        <span style={{ flex: 1, fontWeight: 600, fontSize: 14, color: 'rgba(250,250,250,0.5)' }}>Edit Project</span>
        <SaveButton dirty={dirty} saving={saving} onClick={onSave} />
        <DeleteButton onClick={onDelete} />
      </div>
      <div style={{ flex: 1, overflow: 'auto', padding: 16 }}>
        <div style={{ marginBottom: 12 }}>
          <label style={fieldLabelStyle}>Name</label>
          <InlineInput value={project.name || ''} onChange={(v) => onChange('name', v)} placeholder="Project name" />
        </div>
        <div style={{ marginBottom: 12 }}>
          <label style={fieldLabelStyle}>Description</label>
          <InlineInput type="textarea" value={project.description || ''} onChange={(v) => onChange('description', v || undefined)} placeholder="Description" />
        </div>
        <div style={{ marginBottom: 12 }}>
          <label style={fieldLabelStyle}>Color</label>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {PROJECT_COLORS.map((color, i) => (
              <div
                key={i}
                onClick={() => onChange('color_index', i)}
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: 6,
                  background: color,
                  cursor: 'pointer',
                  border: (project.color_index ?? 0) === i ? '2px solid #fff' : '2px solid transparent',
                }}
              />
            ))}
          </div>
        </div>
        <div style={{ marginBottom: 12 }}>
          <label style={fieldLabelStyle}>Status</label>
          <select
            style={selectStyle}
            value={project.is_completed ? '1' : '0'}
            onChange={(e) => onChange('is_completed', (e.target as HTMLSelectElement).value === '1' ? 1 : 0)}
          >
            <option value="0">Active</option>
            <option value="1">Completed</option>
          </select>
        </div>

        {taskCounts.total > 0 && (
          <div style={{ marginTop: 12, paddingTop: 8, borderTop: '1px solid rgba(250,250,250,0.1)' }}>
            <DetailRow label="Tasks" value={`${taskCounts.completed} / ${taskCounts.total} completed`} />
          </div>
        )}

        <div style={{ marginTop: 12, paddingTop: 8, borderTop: '1px solid rgba(250,250,250,0.1)' }}>
          <DetailRow label="Created" value={formatDate(project.created_at)} />
          <DetailRow label="Updated" value={formatDate(project.updated_at)} />
        </div>
      </div>
    </div>
  )
}

// ── Add subtask input (local copy, mirrors TasksView) ─────────────

function AddSubtaskInput({ onAdd }: { onAdd: (title: string) => void }) {
  const [text, setText] = useState('')
  const submit = () => {
    const t = text.trim()
    if (!t) return
    onAdd(t)
    setText('')
  }
  return (
    <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
      <input
        type="text"
        value={text}
        onInput={(e) => setText((e.target as HTMLInputElement).value)}
        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); submit() } }}
        placeholder="Add subtask..."
        style={{ ...inputStyle, flex: 1, padding: '6px 8px', fontSize: 12 }}
      />
      <button
        onClick={submit}
        disabled={!text.trim()}
        style={{
          padding: '6px 12px',
          border: '1px solid rgba(45,165,160,0.4)',
          borderRadius: 6,
          background: text.trim() ? 'rgba(45,165,160,0.15)' : 'transparent',
          color: text.trim() ? '#2DA5A0' : 'rgba(250,250,250,0.3)',
          cursor: text.trim() ? 'pointer' : 'default',
          fontSize: 12,
        }}
      >
        Add
      </button>
    </div>
  )
}

// ── Create Task Modal (project-aware) ─────────────────────────────

function CreateTaskModal({ defaultProjectId, onClose, onCreate }: {
  defaultProjectId: string
  onClose: () => void
  onCreate: (task: CloudTask) => Promise<void>
}) {
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [priority, setPriority] = useState('1')
  const [status, setStatus] = useState('0')
  const [type, setType] = useState('0')
  const [dueDate, setDueDate] = useState('')
  const [dueTime, setDueTime] = useState('')
  const [location, setLocation] = useState('')
  const [tags, setTags] = useState('')
  const [creating, setCreating] = useState(false)

  const handleCreate = async () => {
    if (!title.trim()) return
    setCreating(true)
    try {
      const now = new Date().toISOString()
      const task: CloudTask = {
        id: crypto.randomUUID(),
        title: title.trim(),
        description: description.trim() || undefined,
        priority: Number(priority),
        status: Number(status),
        type: Number(type),
        dueDate: dueDate ? new Date(`${dueDate}T${dueTime || '00:00'}`).toISOString() : undefined,
        location: location.trim() || undefined,
        projectId: defaultProjectId || undefined,
        tags: tags ? tags.split(',').map((t) => t.trim()).filter(Boolean) : undefined,
        createdAt: now,
        lastUpdatedAt: now,
      }
      await onCreate(task)
    } catch (e) {
      console.error('Failed to create task:', e)
    } finally {
      setCreating(false)
    }
  }

  return (
    <ModalDialog
      title="New Task"
      onClose={onClose}
      footer={
        <>
          <button onClick={onClose} style={{ padding: '6px 16px', border: 'none', borderRadius: 6, background: 'rgba(250,250,250,0.1)', color: 'rgba(250,250,250,0.7)', cursor: 'pointer', fontSize: 13 }}>
            Cancel
          </button>
          <button
            onClick={handleCreate}
            disabled={!title.trim() || creating}
            style={{ padding: '6px 16px', border: 'none', borderRadius: 6, background: '#2DA5A0', color: '#fff', cursor: 'pointer', fontSize: 13, opacity: !title.trim() || creating ? 0.5 : 1 }}
          >
            {creating ? 'Creating...' : 'Create'}
          </button>
        </>
      }
    >
      <FormField label="Title" value={title} onChange={setTitle} required placeholder="Task title" />
      <FormField label="Description" value={description} onChange={setDescription} type="textarea" placeholder="Description" />
      <FormField label="Type" value={type} onChange={setType} type="select" options={TYPE_OPTIONS} />
      <FormField label="Priority" value={priority} onChange={setPriority} type="select" options={PRIORITY_OPTIONS} />
      <FormField label="Status" value={status} onChange={setStatus} type="select" options={STATUS_OPTIONS} />
      <FormField label="Due Date" value={dueDate} onChange={setDueDate} type="date" />
      <FormField label="Due Time" value={dueTime} onChange={setDueTime} type="time" />
      <FormField label="Location" value={location} onChange={setLocation} placeholder="Location" />
      <FormField label="Tags" value={tags} onChange={setTags} placeholder="tag1, tag2, ..." />
    </ModalDialog>
  )
}

// ── Create Project Modal (unchanged) ──────────────────────────────

function CreateProjectModal({ onClose, onCreate }: {
  onClose: () => void
  onCreate: (project: CloudProject) => Promise<void>
}) {
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [colorIndex, setColorIndex] = useState('0')
  const [creating, setCreating] = useState(false)

  const handleCreate = async () => {
    if (!name.trim()) return
    setCreating(true)
    try {
      const now = new Date().toISOString()
      const project: CloudProject = {
        id: crypto.randomUUID(),
        name: name.trim(),
        description: description.trim() || undefined,
        color_index: Number(colorIndex),
        created_at: now,
        updated_at: now,
        is_completed: 0,
      }
      await onCreate(project)
    } catch (e) {
      console.error('Failed to create project:', e)
    } finally {
      setCreating(false)
    }
  }

  return (
    <ModalDialog
      title="New Project"
      onClose={onClose}
      footer={
        <>
          <button onClick={onClose} style={{ padding: '6px 16px', border: 'none', borderRadius: 6, background: 'rgba(250,250,250,0.1)', color: 'rgba(250,250,250,0.7)', cursor: 'pointer', fontSize: 13 }}>
            Cancel
          </button>
          <button
            onClick={handleCreate}
            disabled={!name.trim() || creating}
            style={{ padding: '6px 16px', border: 'none', borderRadius: 6, background: '#2DA5A0', color: '#fff', cursor: 'pointer', fontSize: 13, opacity: !name.trim() || creating ? 0.5 : 1 }}
          >
            {creating ? 'Creating...' : 'Create'}
          </button>
        </>
      }
    >
      <FormField label="Name" value={name} onChange={setName} required placeholder="Project name" />
      <FormField label="Description" value={description} onChange={setDescription} type="textarea" placeholder="Description" />
      <div style={{ marginBottom: 12 }}>
        <label style={{ fontSize: 12, color: 'rgba(250,250,250,0.5)', marginBottom: 4, display: 'block' }}>Color</label>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {PROJECT_COLORS.map((color, i) => (
            <div
              key={i}
              onClick={() => setColorIndex(String(i))}
              style={{
                width: 28,
                height: 28,
                borderRadius: 6,
                background: color,
                cursor: 'pointer',
                border: colorIndex === String(i) ? '2px solid #fff' : '2px solid transparent',
              }}
            />
          ))}
        </div>
      </div>
    </ModalDialog>
  )
}

// ── Styles ─────────────────────────────────────────────────────────

const projectCardStyle: Record<string, string | number> = {
  padding: 16,
  background: 'rgba(0,51,68,0.6)',
  border: '1px solid rgba(250,250,250,0.1)',
  borderRadius: 10,
}

const taskRowStyle: Record<string, string | number> = {
  display: 'flex',
  alignItems: 'center',
  padding: '8px 10px',
  marginBottom: 6,
  background: 'rgba(0,0,0,0.18)',
  border: '1px solid rgba(250,250,250,0.05)',
  borderRadius: 6,
}

const badgeStyle = (bg: string): Record<string, string | number> => ({
  fontSize: 10,
  padding: '1px 6px',
  borderRadius: 3,
  background: bg,
  color: '#fff',
})

const fieldLabelStyle: Record<string, string | number> = {
  fontSize: 12,
  color: 'rgba(250,250,250,0.5)',
  marginBottom: 4,
  display: 'block',
}
